import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, CheckCircle, Car, Clock, Award, GraduationCap, MapPin, Briefcase, Users } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const perks = [
  { icon: Clock, label: 'Flexible Hours', desc: 'Work around your schedule' },
  { icon: Award, label: 'Competitive Pay', desc: 'Rates that value your work' },
  { icon: GraduationCap, label: 'Training Available', desc: 'Develop your skills' },
  { icon: MapPin, label: 'London & Surrounding', desc: 'Work in your area' },
];

const roles = [
  { value: 'residential-cleaner', label: 'Residential Cleaner', desc: 'Home cleaning for private clients' },
  { value: 'commercial-cleaner', label: 'Commercial Cleaner', desc: 'Office and business premises cleaning' },
  { value: 'team-leader', label: 'Team Leader', desc: 'Manage and coordinate cleaning teams' },
  { value: 'cleaning-team', label: 'Cleaning Team / Business', desc: 'Partner with us as a team or business' },
];

const Careers = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    employmentType: '',
    experience: '',
    hasTransport: false,
    availability: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role || !form.employmentType) {
      toast.error('Please select a role and employment type');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      toast.success('Application submitted successfully!');
      setSubmitted(true);
    } catch {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Join Our Team"
        description="Join Serene Sparkle! We're hiring cleaners, team leaders, and partnering with cleaning businesses in London. Flexible hours, competitive pay, training available."
        keywords="cleaning jobs London, cleaner vacancies, cleaning team leader, join cleaning company"
        canonical="/careers"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Join Our Team</h1>
            <p className="text-base sm:text-lg text-slate-600">
              We're growing! Whether you're a self-employed cleaner, a small cleaning business, or looking for a new role — we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {perks.map((perk) => (
              <div key={perk.label} className="flex flex-col items-center text-center p-4">
                <div className="bg-sky-100 rounded-full p-3 mb-3">
                  <perk.icon className="h-6 w-6 text-sky-600" />
                </div>
                <p className="font-semibold text-slate-900 text-sm">{perk.label}</p>
                <p className="text-xs text-slate-500 mt-1">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles & Application */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-5 gap-8">

            {/* Roles Info */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Open Roles</h2>
              <div className="space-y-3">
                {roles.map((role) => (
                  <Card key={role.value} className="border-slate-200">
                    <CardContent className="py-4">
                      <p className="font-semibold text-slate-900">{role.label}</p>
                      <p className="text-sm text-slate-500">{role.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="mt-6 border-sky-200 bg-sky-50">
                <CardContent className="py-4">
                  <h3 className="font-semibold text-slate-900 mb-2">We Welcome:</h3>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" /> Self-employed cleaners</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" /> Small cleaning businesses</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" /> Part-time & full-time applicants</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" /> Experienced team leaders</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="mt-4 border-amber-200 bg-amber-50">
                <CardContent className="py-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Requirements:</h3>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li className="flex items-center gap-2"><Car className="h-4 w-4 text-amber-600 flex-shrink-0" /> Own transport preferred</li>
                    <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-600 flex-shrink-0" /> Based in London or surrounding areas</li>
                    <li className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-amber-600 flex-shrink-0" /> Reliable and professional</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Application Form */}
            <div className="lg:col-span-3">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Apply Now</h2>

              {submitted ? (
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Application Received!</h3>
                    <p className="text-slate-600 mb-4">Thank you for your interest. We'll review your details and get back to you soon.</p>
                    <Button onClick={() => setSubmitted(false)} variant="outline" data-testid="apply-again-btn">
                      Submit another application
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-sky-100" data-testid="application-form">
                  <CardContent className="pt-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="appName">Full Name *</Label>
                          <Input
                            id="appName"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            required
                            placeholder="John Doe"
                            className="mt-1"
                            data-testid="app-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="appEmail">Email *</Label>
                          <Input
                            id="appEmail"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                            required
                            placeholder="john@example.com"
                            className="mt-1"
                            data-testid="app-email"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="appPhone">Phone Number *</Label>
                        <Input
                          id="appPhone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                          required
                          placeholder="07XXX XXXXXX"
                          className="mt-1"
                          data-testid="app-phone"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Role Applying For *</Label>
                          <Select onValueChange={(v) => setForm(f => ({ ...f, role: v }))} value={form.role}>
                            <SelectTrigger className="mt-1" data-testid="app-role">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map(r => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Employment Type *</Label>
                          <Select onValueChange={(v) => setForm(f => ({ ...f, employmentType: v }))} value={form.employmentType}>
                            <SelectTrigger className="mt-1" data-testid="app-type">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full-time">Full-Time</SelectItem>
                              <SelectItem value="part-time">Part-Time</SelectItem>
                              <SelectItem value="self-employed">Self-Employed</SelectItem>
                              <SelectItem value="cleaning-business">Cleaning Business</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="appExperience">Experience *</Label>
                        <Textarea
                          id="appExperience"
                          value={form.experience}
                          onChange={(e) => setForm(f => ({ ...f, experience: e.target.value }))}
                          required
                          rows={3}
                          placeholder="Tell us about your cleaning experience, qualifications, and any relevant skills..."
                          className="mt-1"
                          data-testid="app-experience"
                        />
                      </div>

                      <div>
                        <Label htmlFor="appAvailability">Availability</Label>
                        <Input
                          id="appAvailability"
                          value={form.availability}
                          onChange={(e) => setForm(f => ({ ...f, availability: e.target.value }))}
                          placeholder="e.g., Mon-Fri mornings, weekends only..."
                          className="mt-1"
                          data-testid="app-availability"
                        />
                      </div>

                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="hasTransport"
                          checked={form.hasTransport}
                          onCheckedChange={(checked) => setForm(f => ({ ...f, hasTransport: checked }))}
                          data-testid="app-transport"
                        />
                        <Label htmlFor="hasTransport" className="cursor-pointer">I have my own transport</Label>
                      </div>

                      <div>
                        <Label htmlFor="appMessage">Additional Notes</Label>
                        <Textarea
                          id="appMessage"
                          value={form.message}
                          onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                          rows={2}
                          placeholder="Anything else you'd like us to know..."
                          className="mt-1"
                          data-testid="app-message"
                        />
                      </div>

                      <Button type="submit" disabled={submitting} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="submit-application-btn">
                        <Send className="h-4 w-4 mr-2" />
                        {submitting ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
